import type { FC, HTMLAttributes, RefAttributes } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useModifiers } from '../../styles/hooks/useModifiers';

import styles from './Ripple.module.css';

export enum RippleVariant {
  Single = 'single',
  Multi = 'multi',
}

export interface RippleRef {
  triggerAnimation: () => void;
  stopAnimation: () => void;
}

export interface RippleProps extends HTMLAttributes<HTMLSpanElement>, RefAttributes<RippleRef> {
  variant: RippleVariant;
}

export const Ripple: FC<RippleProps> = forwardRef<RippleRef, RippleProps>(
  ({ children, className, variant, ...rest }, ref) => {
    const [animate, setAnimate] = useState(false);
    const rippleElementRef = useRef<HTMLSpanElement>(null);

    const mods = useModifiers(
      styles,
      'ripple',
      { [variant]: true },
      { withBaseClass: true, extraClassName: className }
    );

    useImperativeHandle(ref, () => {
      return {
        triggerAnimation: (): void => setAnimate(true),
        stopAnimation: (): void => setAnimate(false),
      };
    }, []);

    useEffect(() => {
      const element = rippleElementRef.current;

      if (!element) return;

      const finishAnimation = (): void => {
        setAnimate(false);
      };

      element.addEventListener('animationend', finishAnimation);
      return (): void => element.removeEventListener('animationend', finishAnimation);
    }, []);

    const rippleWaves = useMemo(() => {
      const waveCount = variant === RippleVariant.Multi ? 3 : 1;
      return Array.from({ length: waveCount }).map((_, index) => {
        const idx = index;
        return <span key={idx} className={styles.ripple__wave} />;
      });
    }, [variant]);

    return (
      <span className={mods} {...rest} ref={rippleElementRef}>
        {animate ? rippleWaves : null}
        {children}
      </span>
    );
  }
);

Ripple.displayName = 'Ripple';
